import React, { useMemo } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { FilterControl } from '../../components/filters/FilterControls';
import FilterDropdownSelect from '../../components/filters/FilterDropdownSelect';
import FilterMultiSelect from '../../components/filters/FilterMultiSelect';
import '../../components/filters/FilterPanel.css';
import { Group, Workflow } from '../../types';
import { TICKET_EXPORT_STATUS_OPTIONS } from './ticketExportStatusOptions';
import {
  CLOSED_ITEMS_HIDE_AFTER_DAYS,
  CLOSED_VISIBILITY_OPTIONS,
  EXPORT_DEFAULT_CLOSED_VISIBILITY,
  closedVisibilityNotice,
  normalizeClosedVisibility,
  type ClosedVisibility,
} from '../../utils/closedItemsVisibility';
import type { ExportArea } from './api';

export interface ExportFilterValues {
  recordId: string;
  searchText: string;
  filterWorkflow: string;
  filterGroup: string;
  /** Requisições: seleção única. Chamados: use filterStatuses. */
  filterStatus: string;
  /** Chamados: multiseleção; vazio = todos. */
  filterStatuses: string[];
  filterPriority: string;
  dateFrom: string;
  dateTo: string;
  includeSubtasks: boolean;
  closedVisibility: ClosedVisibility;
}

export function createEmptyExportFilterValues(): ExportFilterValues {
  return {
    recordId: '',
    searchText: '',
    filterWorkflow: '',
    filterGroup: '',
    filterStatus: '',
    filterStatuses: [],
    filterPriority: '',
    dateFrom: '',
    dateTo: '',
    includeSubtasks: false,
    closedVisibility: EXPORT_DEFAULT_CLOSED_VISIBILITY,
  };
}

/** Garante `filterStatuses` e demais campos quando o estado veio de versão anterior ou merge parcial. */
export function normalizeExportFilterValues(values: Partial<ExportFilterValues>): ExportFilterValues {
  const base = createEmptyExportFilterValues();
  return {
    ...base,
    ...values,
    filterStatuses: Array.isArray(values.filterStatuses) ? values.filterStatuses : [],
    closedVisibility: normalizeClosedVisibility(values.closedVisibility ?? base.closedVisibility),
  };
}

interface Props {
  area: ExportArea;
  values: ExportFilterValues;
  onChange: (patch: Partial<ExportFilterValues>) => void;
  workflows: Workflow[];
  groups: Group[];
}

const ExportFilters: React.FC<Props> = ({ area, values, onChange, workflows, groups }) => {
  const filters = normalizeExportFilterValues(values);
  const idLabel = area === 'tickets' ? 'ID do chamado' : 'ID da requisição';
  const conclusionNouns =
    area === 'tickets'
      ? { singular: 'chamado', plural: 'chamados' }
      : { singular: 'requisição', plural: 'requisições' };
  const conclusionNotice =
    filters.closedVisibility === 'all'
      ? 'Exibindo todo o histórico, sem recorte por data de conclusão.'
      : closedVisibilityNotice(filters.closedVisibility, 0, conclusionNouns) ||
        `Ocultando concluídos há mais de ${CLOSED_ITEMS_HIDE_AFTER_DAYS} dias.`;
  const idHelp =
    area === 'tickets'
      ? 'Se preenchido, apenas este chamado será exportado.'
      : 'Aceita o ID público (10001 ou 10001.1). Se preenchido, apenas este registro será exportado.';

  const workflowOptions = useMemo(
    () => workflows.map((workflow) => ({ value: String(workflow.id), label: workflow.name })),
    [workflows],
  );
  const groupOptions = useMemo(
    () => groups.map((group) => ({ value: String(group.id), label: group.name })),
    [groups],
  );
  const priorityOptions = useMemo(
    () => [
      { value: 'p1', label: 'P1' },
      { value: 'p2', label: 'P2' },
      { value: 'p3', label: 'P3' },
      { value: 'p4', label: 'P4' },
    ],
    [],
  );
  const closedVisibilityOptions = useMemo(
    () => CLOSED_VISIBILITY_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
    [],
  );
  const purchaseStatusOptions = useMemo(
    () => TICKET_EXPORT_STATUS_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
    [],
  );

  return (
    <Card className="mb-3 dashboard-section-card">
      <Card.Header className="d-flex align-items-center gap-2 py-2">
        <i className="bi bi-funnel text-primary" aria-hidden />
        <span className="fw-semibold">Filtros</span>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label">{idLabel}</Form.Label>
              <FilterControl
                type={area === 'tickets' ? 'number' : 'text'}
                placeholder={area === 'tickets' ? 'Digite o ID...' : 'Ex.: 10001.1'}
                value={filters.recordId}
                onChange={(event) => onChange({ recordId: event.target.value })}
              />
              <Form.Text className="text-muted">{idHelp}</Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label">Buscar por texto</Form.Label>
              <FilterControl
                type="text"
                placeholder="Título ou descrição"
                value={filters.searchText}
                onChange={(event) => onChange({ searchText: event.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label" htmlFor="export-filter-workflow">
                Fluxo
              </Form.Label>
              <FilterDropdownSelect
                id="export-filter-workflow"
                value={filters.filterWorkflow}
                onChange={(next) => onChange({ filterWorkflow: next })}
                options={workflowOptions}
                placeholderLabel="Todos"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label" htmlFor="export-filter-group">
                Grupo
              </Form.Label>
              <FilterDropdownSelect
                id="export-filter-group"
                value={filters.filterGroup}
                onChange={(next) => onChange({ filterGroup: next })}
                options={groupOptions}
                placeholderLabel="Todos"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label" htmlFor="export-filter-status">
                Status
              </Form.Label>
              {area === 'tickets' ? (
                <>
                  <FilterMultiSelect
                    id="export-filter-status"
                    options={[...TICKET_EXPORT_STATUS_OPTIONS]}
                    selected={filters.filterStatuses}
                    onChange={(next) => onChange({ filterStatuses: next })}
                  />
                  <Form.Text className="text-muted export-status-help">
                    Segure Ctrl (Cmd no Mac) e clique para combinar status. Sem seleção: todos.
                  </Form.Text>
                </>
              ) : (
                <FilterDropdownSelect
                  id="export-filter-status"
                  value={filters.filterStatus}
                  onChange={(next) => onChange({ filterStatus: next })}
                  options={purchaseStatusOptions}
                  placeholderLabel="Todos"
                />
              )}
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label" htmlFor="export-filter-priority">
                Prioridade
              </Form.Label>
              <FilterDropdownSelect
                id="export-filter-priority"
                value={filters.filterPriority}
                onChange={(next) => onChange({ filterPriority: next })}
                options={priorityOptions}
                placeholderLabel="Todas"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label" htmlFor="export-filter-closed-visibility">
                Conclusão
              </Form.Label>
              <FilterDropdownSelect
                id="export-filter-closed-visibility"
                value={filters.closedVisibility}
                onChange={(next) => onChange({ closedVisibility: normalizeClosedVisibility(next) })}
                options={closedVisibilityOptions}
                showEmptyOption={false}
                placeholderLabel=""
              />
              <Form.Text className="text-muted export-conclusion-help">
                Vale para itens fechados e cancelados, pela data de conclusão. {conclusionNotice}
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label">Criado de</Form.Label>
              <FilterControl
                type="date"
                value={filters.dateFrom}
                onChange={(event) => onChange({ dateFrom: event.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label">Criado até</Form.Label>
              <FilterControl
                type="date"
                value={filters.dateTo}
                onChange={(event) => onChange({ dateTo: event.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Check
              type="switch"
              id="export-include-subtasks"
              label="Incluir subtarefas em aba separada"
              checked={filters.includeSubtasks}
              onChange={(event) => onChange({ includeSubtasks: event.target.checked })}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default ExportFilters;
